import React, {useState} from 'react';
import moment from 'moment';

import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import DownloadIcon from '@mui/icons-material/Download';
import Grid from '@mui/material/Grid';
import IconButton from '@mui/material/IconButton';
import ExportIcon from '@mui/icons-material/CloudDownload';
import Tooltip from '@mui/material/Tooltip';

import useUser from '../../hooks/useUser';
import {useCreateConsolidatedFileMutation} from '../../redux/services/consolidatedFileApi';
import CustomDialog from '../common/CustomDialog';
import ErrorAlert from '../common/ErrorAlert';
import FormInput from '../common/FormInput';

const formFields = ({patientId}) => [
  {
    name: 'patientId',
    label: 'Patient',
    type: 'selectPatient',
    defaultValue: patientId,
    color: 'default',
    providerOnly: true,
    required: true,
    grid: {xs: 12, md: 6},
  },
  {
    name: 'fileType',
    label: 'File Type',
    type: 'select',
    defaultValue: 'note',
    options: [
      {value: 'note', label: 'Chart Notes'},
      {value: 'invoice', label: 'Superbills'},
    ],
    required: true,
    grid: {xs: 6, md: 3},
    fullWidth: true,
    maxWidth: 200,
  },
  {
    name: 'sessionCategory',
    label: 'Session Category',
    type: 'select',
    defaultValue: null,
    options: [
      {value: 'optimize', label: 'Optimize'},
      {value: 'restore', label: 'Restore'},
    ],
    required: false,
    grid: {xs: 6, md: 3},
    fullWidth: true,
    maxWidth: 200,
  },
  {
    name: 'startDate',
    label: 'Start Date',
    type: 'date',
    required: false,
    grid: {xs: 6, md: 6},
  },
  {
    name: 'endDate',
    label: 'End Date',
    type: 'date',
    required: false,
    grid: {xs: 6, md: 6},
  },
];

const handleSubmit = ({formValues, createFile}) => async e => {
  const response = await createFile({
    patient_id: formValues.patientId,
    file_type: formValues.fileType,
    session_category: formValues.sessionCategory || null,
    start_date: (
      formValues.startDate 
      ? moment(formValues.startDate)
        .startOf('day')
        .toISOString()
      : null
    ),
    end_date: (
      formValues.endDate 
      ? moment(formValues.endDate)
        .endOf('day')
        .toISOString()
      : null
    ),
  });
};

const handleFieldUpdate = ({formValues, setFormValues}) => fieldName => (e, val) => {
  const value = val?.patientId || e?.patientId || e.target.value;
  setFormValues({
    ...formValues,
    [fieldName]: value,
  });
};

const CreateConsolidatedFileForm = ({patientId}) => {
  const [formValues, setFormValues] = useState({
    patientId,
    fileType: 'note',
  });
  const [createFile, response] = useCreateConsolidatedFileMutation();
  const url = response?.data?.data?.[0]?.url;
  const onSubmit = handleSubmit({formValues, createFile});
  const user = useUser();
  const isProvider = !!user?.provider;
  return (
    <>
      <ErrorAlert error={response?.error} />
      <Grid container spacing={1} padding={2}>
        {response?.isLoading && 
          <Grid item xs={12}>
            <CircularProgress size={18} />
          </Grid>
        }
        {formFields({patientId}).map(f => (
          <Grid item xs={f?.grid?.xs || 12} md={f?.grid?.md || 12} key={f.name}>
            {/* Render form field based on its type */}
            {/* This is a placeholder; actual implementation would depend on the form library being used */}
            <FormInput 
              defaultValue={f.defaultValue}
              onChange={handleFieldUpdate({formValues, setFormValues})(f.name)}
              formValues={formValues}
              {...f}
              disabled={f.providerOnly && !isProvider}
            />
          </Grid>
        ))}
        <Grid item xs={12} md={6} display="flex">
          <Box>
            <Button 
              variant="contained" 
              color="primary" 
              onClick={onSubmit}
              disabled={
                response.isLoading 
                || !['note', 'invoice'].includes(formValues.fileType)
                || !formValues.patientId
              }
            >
              Generate Export
            </Button>
          </Box>
        </Grid>
        <Grid item xs={12} md={6} display="flex">
          <Box>
            {url && 
              <Button 
                variant="contained" 
                color="success" 
                disabled={response.isLoading}
                href={url}
                target="_blank"
              >
                <DownloadIcon sx={{mr: 1}} />
                Download File
              </Button>
            }
          </Box>
        </Grid>
      </Grid>
    </>
  );
};

const CreateConsolidatedFormDialog = ({patientId, isOpen, onClose}) => {
  return (
    <CustomDialog 
      title='Create Patient Data Export'
      isOpen={isOpen} 
      onClose={onClose} 
      maxWidth="sm"
    >
      <CreateConsolidatedFileForm patientId={patientId} />
    </CustomDialog>
  );
};

const CreateConsolidatedFormButton = ({
  patientId, 
  iconOnly = false, 
  variant='contained',
  size = 'medium',
  color='primary',
}) => {
  const [isOpen, setIsOpen] = useState(false);
  return (
    <>
      <CreateConsolidatedFormDialog 
        patientId={patientId}
        isOpen={isOpen}
        onClose={() => setIsOpen(false)}
      />
      {iconOnly ? 
        <Tooltip title='Export Data'>
          <IconButton color={color} onClick={() => setIsOpen(true)}>
            <ExportIcon />
          </IconButton>
        </Tooltip>
        : <Button 
          variant={variant}
          color={color}
          size={size}
          onClick={() => setIsOpen(true)}
        >
          <ExportIcon sx={{mr: 1}} />
          Export
        </Button>
    }
    </>
  );
};

export default CreateConsolidatedFormButton;